// Taker /waitlist/:slug — "tell me when the next live is" capture page.
//
// Companion to the ReplayPage soft-landing flow:
//   1. Stranger hits /replays/:slug?email=x, isn't on the registration list
//   2. ReplayPage shows "Not registered? Tell me when the next live is →"
//   3. Click → lands here with ?email=x prefilled
//   4. Submit → POST /api/public/events/:slug/waitlist → success state
//
// Also directly linked from the engine LP's replay-phase blocks via the
// `nextCohortCallout` block so visitors who land on /events/:slug while
// the cohort is in replay have a non-looping path to express interest.
//
// Borrows the brand chrome (siteNav + footer) from the assessment's public
// config the same way ReplayPage does, so the page feels native to the brand
// rather than a stripped-down white card.

import { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { recordLandingView } from './api';
import { Block } from './components/Blocks';
import { BrandProvider } from './components/BrandContext';
import { CountrySelect } from './components/CountrySelect';
import { SmsOptIn } from './components/SmsOptIn';
import type { ContentBlock, ResolvedBrand } from './types';
import { useBrandGtm } from './lib/useBrandGtm';
import { applyBranding } from './theme';

const API = import.meta.env.VITE_API_BASE_URL as string;

interface BrandShell {
  brand: ResolvedBrand | null;
  navBlock: ContentBlock | null;
  footerBlock: ContentBlock | null;
  title: string | null;
}

export default function WaitlistPage() {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  // Same convention as ReplayPage + InlineRegistrationFormRender — accept
  // `?email=` and `?utm_email=` so marketing-tool decorations work.
  const urlEmail = (searchParams.get('email') ?? searchParams.get('utm_email') ?? '').trim();
  const urlFirstName = (searchParams.get('firstName') ?? searchParams.get('first_name') ?? '').trim();
  // `source` lets us track where the submission came from in the activity
  // log — defaults to a sensible label if not present.
  const source = (searchParams.get('source') ?? '').trim() || 'waitlist-page';

  const [email, setEmail] = useState(urlEmail);
  const [firstName, setFirstName] = useState(urlFirstName);
  const [lastName, setLastName] = useState('');
  const [country, setCountry] = useState('');
  const [step, setStep] = useState<'email' | 'details'>('email');
  const [state, setState] = useState<'form' | 'submitting' | 'success' | 'error'>('form');
  const [errMsg, setErrMsg] = useState<string>('');
  const [brandShell, setBrandShell] = useState<BrandShell>({ brand: null, navBlock: null, footerBlock: null, title: null });
  const [shellSettled, setShellSettled] = useState(false);

  // Brand-aware GTM — event waitlist landings are a tracked funnel step.
  useBrandGtm(brandShell.brand?.gtmContainerId, shellSettled);

  useEffect(() => {
    if (slug) recordLandingView(slug);
  }, [slug]);

  // Hydrate brand chrome from the assessment's public config, mirroring the
  // ReplayPage pattern. The CTA in the siteNav is hidden — the page's
  // primary action (waitlist form) is already front-and-center.
  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API}/api/assessments/${encodeURIComponent(slug)}?phase=waitlist`);
        if (!res.ok) return;
        const json = await res.json() as { assessment?: { config?: Record<string, unknown>; brand?: Record<string, unknown> | null; title?: string } };
        const cfg = json.assessment?.config ?? {};
        const branding = (cfg as { branding?: Record<string, unknown> }).branding ?? null;
        const intro = (cfg as { intro?: { blocks?: ContentBlock[] } }).intro ?? {};
        const blocks = intro.blocks ?? [];
        const navBlock = blocks.find((b) => b.type === 'siteNav') ?? null;
        const htmlBlocks = blocks.filter((b) => b.type === 'html');
        const footerBlock = htmlBlocks.length > 0 ? htmlBlocks[htmlBlocks.length - 1] : null;
        const brand = (json.assessment?.brand ?? null) as ResolvedBrand | null;
        if (!cancelled) {
          setBrandShell({ brand, navBlock, footerBlock, title: json.assessment?.title ?? null });
          if (branding) {
            try { applyBranding(branding as never, { brand: brand as never }); } catch { /* ignore */ }
          }
        }
      } catch { /* non-fatal — page still works without chrome */ }
    })().finally(() => { if (!cancelled) setShellSettled(true); });
    return () => { cancelled = true; };
  }, [slug]);

  const heading = useMemo(() => {
    const title = brandShell.title;
    return title ? `Next live ${title}` : 'Next live cohort';
  }, [brandShell.title]);

  async function postWaitlist(body: Record<string, unknown>): Promise<boolean> {
    const res = await fetch(`${API}/api/public/events/${encodeURIComponent(slug!)}/waitlist`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      const detail = await res.json().catch(() => ({} as { error?: string; reason?: string }));
      const reason = (detail as { reason?: string }).reason;
      setErrMsg(reason && reason !== 'invalid_format'
        ? "That email domain doesn't accept mail — please double-check it."
        : (detail as { error?: string }).error ?? 'Something went wrong. Try again in a moment.');
      setState('error');
      return false;
    }
    return true;
  }

  // Step 1 — email only. The waitlist seat is saved the moment it lands.
  async function submitEmail(e: React.FormEvent) {
    e.preventDefault();
    if (!slug) return;
    const trimmedEmail = email.trim();
    if (!trimmedEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      setErrMsg('Please enter a valid email address.');
      return;
    }
    setErrMsg('');
    setState('submitting');
    try {
      if (await postWaitlist({ email: trimmedEmail, source })) { setState('form'); setStep('details'); }
    } catch (err) {
      console.error('waitlist submit failed', err);
      setErrMsg('Network error. Try again in a moment.');
      setState('error');
    }
  }

  // Step 2 — first name + last name + country (COALESCEd onto the same
  // entry). All three required — the minimum every opt-in captures
  // alongside email (2026-09-13, Michael).
  async function submitDetails(e: React.FormEvent) {
    e.preventDefault();
    if (!slug) return;
    const trimmedFirst = firstName.trim();
    const trimmedLast = lastName.trim();
    if (!trimmedFirst) { setErrMsg('Please enter your first name.'); return; }
    if (!trimmedLast) { setErrMsg('Please enter your last name.'); return; }
    if (!country) { setErrMsg('Please select your country.'); return; }
    setErrMsg('');
    setState('submitting');
    try {
      if (await postWaitlist({ email: email.trim(), firstName: trimmedFirst, lastName: trimmedLast, source, country })) {
        setState('success');
      }
    } catch (err) {
      console.error('waitlist submit failed', err);
      setErrMsg('Network error. Try again in a moment.');
      setState('error');
    }
  }

  return (
    <BrandProvider brand={brandShell.brand}>
      <div style={{
        minHeight: '100vh',
        background: 'var(--color-bg, #FAFAF7)',
        color: 'var(--color-text, #0E1E3A)',
        display: 'flex',
        flexDirection: 'column',
      }}>
        {brandShell.navBlock && (
          <Block block={{
            ...(brandShell.navBlock as unknown as Record<string, unknown>),
            cta: null,
            ctaByPhase: undefined,
          } as unknown as ContentBlock} />
        )}
        <div style={{ flex: 1, padding: '24px 16px 64px' }}>
          <div style={{ maxWidth: 520, margin: '60px auto 0' }}>
            {state === 'success' ? (
              <SuccessPanel heading={heading} email={email} firstName={firstName} />
            ) : (
              <FormCard
                heading={heading}
                step={step}
                email={email}
                firstName={firstName}
                lastName={lastName}
                country={country}
                busy={state === 'submitting'}
                err={state === 'error' ? errMsg : (errMsg && state !== 'submitting' ? errMsg : '')}
                onEmailChange={setEmail}
                onFirstNameChange={setFirstName}
                onLastNameChange={setLastName}
                onCountryChange={setCountry}
                onSubmit={step === 'email' ? submitEmail : submitDetails}
              />
            )}
          </div>
        </div>
        {brandShell.footerBlock && <Block block={brandShell.footerBlock} />}
      </div>
    </BrandProvider>
  );
}

function FormCard({
  heading, step, email, firstName, lastName, country, busy, err,
  onEmailChange, onFirstNameChange, onLastNameChange, onCountryChange, onSubmit,
}: {
  heading: string;
  step: 'email' | 'details';
  email: string;
  firstName: string;
  lastName: string;
  country: string;
  busy: boolean;
  err: string;
  onEmailChange: (v: string) => void;
  onFirstNameChange: (v: string) => void;
  onLastNameChange: (v: string) => void;
  onCountryChange: (v: string) => void;
  onSubmit: (e: React.FormEvent) => void;
}) {
  const inputStyle: React.CSSProperties = {
    width: '100%', padding: '12px 14px', fontSize: 15, boxSizing: 'border-box',
    border: '1px solid #E5E3DF', borderRadius: 8, marginBottom: 14,
    background: '#FAFAF7', color: '#0E1E3A',
  };
  const labelStyle: React.CSSProperties = { display: 'block', fontSize: 13, fontWeight: 600, color: '#3F3F46', marginBottom: 6 };
  const buttonStyle: React.CSSProperties = {
    width: '100%', padding: '12px 18px', fontSize: 15, fontWeight: 600,
    background: 'var(--color-primary, #F05A28)', color: 'white',
    border: 'none', borderRadius: 8, cursor: busy ? 'not-allowed' : 'pointer',
  };
  return (
    <form
      onSubmit={onSubmit}
      style={{ background: '#FFFFFF', border: '1px solid #E5E3DF', borderRadius: 14, padding: '36px 32px' }}
    >
      <h1 style={{ fontFamily: 'Fraunces, Georgia, serif', fontSize: 28, margin: '0 0 6px', fontWeight: 600 }}>
        {heading}
      </h1>
      {step === 'email' ? (
        <>
          <p style={{ fontSize: 14, color: '#6B6B6B', margin: '0 0 24px', lineHeight: 1.5 }}>
            The next live date isn&apos;t locked yet — drop your email and we&apos;ll send you the new date the moment it&apos;s set, with a one-click link to save your seat.
          </p>
          <label style={labelStyle}>Email</label>
          <input
            type="email" autoComplete="email" autoFocus required placeholder="you@example.com"
            value={email} onChange={(e) => onEmailChange(e.target.value)} disabled={busy}
            style={{ ...inputStyle, marginBottom: 16 }}
          />
          <button type="submit" disabled={busy || !email.trim()} style={buttonStyle}>
            {busy ? 'Adding you…' : 'Tell me when the next live is →'}
          </button>
        </>
      ) : (
        <>
          <p style={{ fontSize: 14, color: '#6B6B6B', margin: '0 0 24px', lineHeight: 1.5 }}>
            You&apos;re on the list. Just your name and where you&apos;re based, so we get the details right.
          </p>
          <label style={labelStyle}>First name</label>
          <input
            type="text" autoComplete="given-name" placeholder="Your first name" autoFocus required
            value={firstName} onChange={(e) => onFirstNameChange(e.target.value)} disabled={busy}
            style={inputStyle}
          />
          <label style={labelStyle}>Last name</label>
          <input
            type="text" autoComplete="family-name" placeholder="Your last name" required
            value={lastName} onChange={(e) => onLastNameChange(e.target.value)} disabled={busy}
            style={inputStyle}
          />
          <label style={labelStyle}>Country</label>
          <CountrySelect value={country} onChange={onCountryChange} style={{ ...inputStyle, marginBottom: 16 }} />
          <button type="submit" disabled={busy || !firstName.trim() || !lastName.trim() || !country} style={buttonStyle}>
            {busy ? 'Saving…' : 'Done'}
          </button>
        </>
      )}
      {err && (
        <div style={{
          marginTop: 14, padding: '10px 12px',
          background: 'rgba(220,38,38,0.06)',
          border: '1px solid rgba(220,38,38,0.25)',
          borderRadius: 6, fontSize: 13, color: '#991b1b',
        }}>{err}</div>
      )}
    </form>
  );
}

function SuccessPanel({ heading, email, firstName }: { heading: string; email: string; firstName: string }) {
  return (
    <div style={{
      background: '#FFFFFF',
      border: '1px solid #E5E3DF',
      borderRadius: 14, padding: '36px 32px',
      textAlign: 'center',
    }}>
      <div style={{
        width: 56, height: 56, borderRadius: '50%',
        background: 'rgba(16,185,129,0.1)',
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        marginBottom: 18,
      }}>
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M20 6L9 17L4 12" stroke="#10B981" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>
      <h1 style={{ fontFamily: 'Fraunces, Georgia, serif', fontSize: 26, margin: '0 0 10px', fontWeight: 600 }}>
        You&apos;re on the list
      </h1>
      <p style={{ fontSize: 15, color: '#3F3F46', margin: '0 0 6px', lineHeight: 1.55 }}>
        We&apos;ll email <strong>{email}</strong> the moment the next {heading.toLowerCase().replace(/^next live /, '')} date is set.
      </p>
      <p style={{ fontSize: 13, color: '#6B6B6B', margin: '14px 0 0', lineHeight: 1.5 }}>
        Nothing else to do right now — keep an eye on your inbox.
      </p>
      {/* Email-first: the seat is already saved. Offer a text as an optional add-on
          so they can hear the moment a date is set, not just by email. */}
      {/^\S+@\S+\.\S+$/.test(email) && (
        <div style={{ marginTop: 20, textAlign: 'left' }}>
          <SmsOptIn
            email={email}
            firstName={firstName || null}
            source="waitlist"
            heading="Want a text when the date lands?"
          />
        </div>
      )}
    </div>
  );
}
